import { useEffect, useRef, useState } from 'react';
import { api, type Agent, type ChatThreadSummary, type ChatMessage } from '../api';
import { readSSE } from '../lib/sse';
import { Button, Markdown } from '../ui';

/**
 * Chat embutido no editor de docs — SEMPRE visível. Um seletor no topo lista as
 * conversas existentes do projeto (as mesmas da aba Chat) e permite iniciar uma nova
 * com um agente. O agente edita o arquivo aberto (docPath vai como contexto); ao fim
 * do turno chama onAfterTurn(shaAntes) pra o editor recarregar e mostrar o diff.
 */
export function DocChat({
  slug,
  projectId,
  docPath,
  onAfterTurn,
  selection,
  onClearSelection,
}: {
  slug: string;
  projectId: string;
  docPath: string;
  onAfterTurn: (shaBefore: string | null) => void;
  /** Trecho selecionado no editor — enviado com foco pro agente. */
  selection?: string | null;
  onClearSelection?: () => void;
}) {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [threads, setThreads] = useState<ChatThreadSummary[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  function loadThreads() {
    api.listThreads().then((r) => setThreads(r.threads.filter((t) => t.projectId === projectId))).catch(() => undefined);
  }
  useEffect(() => {
    api.listAgents(slug).then((r) => setAgents(r.agents.filter((a) => a.enabled && !a.isRouter))).catch(() => undefined);
    loadThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, projectId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, streaming]);

  async function openExisting(id: string) {
    try {
      const { messages: msgs } = await api.getThread(id);
      setThreadId(id);
      setMessages(msgs);
      setErr(null);
    } catch { setErr('Falha ao abrir conversa'); }
  }

  async function startNew(agent: Agent) {
    try {
      const { thread } = await api.createThread({ projectId, agentId: agent.id, mode: 'direct' });
      setThreadId(thread.id);
      setMessages([]);
      setErr(null);
      loadThreads();
    } catch { setErr('Falha ao criar conversa'); }
  }

  function onSelect(value: string) {
    if (value.startsWith('t:')) openExisting(value.slice(2));
    else if (value.startsWith('a:')) {
      const a = agents.find((x) => x.id === value.slice(2));
      if (a) startNew(a);
    }
  }

  async function send() {
    if (!threadId || !input.trim() || sending) return;
    const content = input.trim();
    setInput('');
    setSending(true);
    setErr(null);
    const tid = threadId;
    setMessages((m) => [...m, { id: `u-${Date.now()}`, threadId: tid, role: 'user', content, toolInfo: null, createdAt: new Date().toISOString() }]);
    let shaBefore: string | null = null;
    try { const h = await api.docHistory(slug, docPath); shaBefore = h.versions[0]?.sha ?? null; } catch { /* sem histórico */ }
    let acc = '';
    setStreaming('');
    try {
      const payload: Record<string, unknown> = { content, docPath };
      if (selection && selection.trim()) payload['selection'] = selection;
      const res = await fetch(`/v1/chat/threads/${tid}/messages`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok || !res.body) { setErr(`Erro HTTP ${res.status}`); return; }
      await readSSE(res.body, (ev, data) => {
        if (ev === 'text_delta') { acc += String(data['delta'] ?? ''); setStreaming(acc); }
        else if (ev === 'done') {
          const ft = typeof data['finalText'] === 'string' && data['finalText'] ? (data['finalText'] as string) : acc;
          setMessages((m) => [...m, { id: `a-${Date.now()}`, threadId: tid, role: 'assistant', content: ft, toolInfo: null, createdAt: new Date().toISOString() }]);
        } else if (ev === 'error') { setErr(String(data['message'] ?? 'erro')); }
      });
    } catch (e) {
      setErr(String(e));
    } finally {
      setSending(false);
      setStreaming(null);
      onAfterTurn(shaBefore);
      onClearSelection?.();
    }
  }

  return (
    <div className="doc-chat">
      <div className="doc-chat-head">
        <strong style={{ fontSize: 13 }}>💬 IA</strong>
        <select
          className="doc-chat-select"
          value={threadId ? `t:${threadId}` : ''}
          onChange={(e) => onSelect(e.target.value)}
        >
          <option value="">— escolher conversa / nova —</option>
          {threads.length > 0 && (
            <optgroup label="Conversas existentes">
              {threads.map((t) => (
                <option key={t.id} value={`t:${t.id}`}>{t.agentName ? `${t.agentName}: ` : ''}{t.title}</option>
              ))}
            </optgroup>
          )}
          <optgroup label="Nova conversa com">
            {agents.map((a) => (
              <option key={a.id} value={`a:${a.id}`}>{a.name}{a.scope === 'docs' ? ' ✏️' : ''}</option>
            ))}
          </optgroup>
        </select>
      </div>

      <div className="doc-chat-msgs">
        {!threadId ? (
          <div className="doc-chat-empty">Escolha uma conversa existente acima ou inicie uma nova com um agente (✏️ edita docs).</div>
        ) : (
          <>
            {messages.length === 0 && streaming == null && (
              <div className="doc-chat-empty">Peça uma alteração: "deixe a introdução mais clara", "adicione exemplos"…</div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`doc-chat-msg is-${m.role}`}>
                <div className="doc-chat-role">{m.role === 'user' ? 'você' : 'IA'}</div>
                {m.role === 'assistant' ? <Markdown text={m.content} /> : <div className="doc-chat-usertext">{m.content}</div>}
              </div>
            ))}
            {streaming != null && (
              <div className="doc-chat-msg is-assistant">
                <div className="doc-chat-role">IA</div>
                <Markdown text={streaming || '…'} />
              </div>
            )}
          </>
        )}
        <div ref={bottomRef} />
      </div>

      {selection && selection.trim() && (
        <div className="ai-chat-selection" title={selection}>
          <span className="ai-chat-selection-label">trecho selecionado ({selection.length} car.) — a IA vai focar nele</span>
          {onClearSelection && (
            <button type="button" className="ai-chat-selection-clear" onClick={onClearSelection} title="Limpar foco">✕</button>
          )}
        </div>
      )}
      <div className="doc-chat-input">
        <input
          placeholder={threadId ? 'peça uma alteração no documento…' : 'escolha uma conversa acima pra começar'}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          disabled={!threadId || sending}
        />
        <Button size="sm" variant="primary" onClick={send} disabled={!threadId || sending || !input.trim()}>{sending ? '…' : 'enviar'}</Button>
      </div>
      {err && <div className="doc-chat-err">{err}</div>}
    </div>
  );
}
